/**
 * RunOverviewDashboard
 *
 * The main Reports landing page for a selected run.
 * Answers the questions that matter:
 *   1. What did we find?        → Metric strip (asset counts)
 *   2. Is the pipeline done?    → Pipeline progress
 *   3. What is this app?        → Application composition
 *   4. Where is the risk?       → Code risk ranking (horizontal bar)
 *   5. Is it ready?             → Quality gate
 *   6. What next?               → Report navigation
 *
 * Visual language matches the Analyze screen (ga-dash-card / ga-dash-panel pattern).
 * No scatter charts. No decorative visuals. Every element answers a question.
 *
 * Props:
 *   accountId       — selected account ID
 *   appName         — selected application name
 *   workflowRunId   — selectedRun.workflow_run_id (used to match MongoDB run)
 *   onReportClick   — callback(item) to open a report
 *   reportGroups    — REPORT_NAV_GROUPS array
 *   getJobForType   — function(flowType) → job or null
 */

import React, { useState, useEffect, useRef, useMemo } from 'react'
import * as echarts from 'echarts'
import { Loader2, ChevronRight } from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './RunOverviewDashboard.css'

function authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Pipeline node config ──────────────────────────────────────────────────────

const PIPELINE_NODES = [
  { key: 'ingest',             label: 'Ingest'     },
  { key: 'code_analysis',      label: 'Analysis'   },
  { key: 'code_refactor',      label: 'Refactor'   },
  { key: 'ai_naming',          label: 'AI Naming'  },
  { key: 'service_generation', label: 'Svc Gen'    },
  { key: 'pre_flight',         label: 'Pre-flight' },
  { key: 'java_package',       label: 'Build'      },
]

// ── PipelineFlow ──────────────────────────────────────────────────────────────

function PipelineFlow({ nodesCompleted }) {
  const completedCount = PIPELINE_NODES.filter(n => nodesCompleted.includes(n.key)).length
  const pct = Math.round((completedCount / PIPELINE_NODES.length) * 100)

  return (
    <div className="rod-panel rod-pipeline-wrap">
      <div className="rod-panel-header">
        <span className="rod-panel-title">Pipeline Progress</span>
        <span className="rod-panel-badge">{completedCount} / {PIPELINE_NODES.length} stages · {pct}%</span>
      </div>
      <div className="rod-pipeline">
        {PIPELINE_NODES.map(({ key, label }) => {
          const done = nodesCompleted.includes(key)
          return (
            <div key={key} className={`rod-pipeline-node ${done ? 'done' : 'pending'}`}>
              <div className={`rod-node-circle ${done ? 'done' : 'pending'}`}>
                {done ? '✓' : ''}
              </div>
              <div className="rod-node-label">{label}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Application composition bar ───────────────────────────────────────────────
// Mirrors the ga-comp-row pattern from the Analyze screen.

const COMP_TYPES = [
  { key: 'cobol_programs', label: 'COBOL',      color: '#388bfd' },
  { key: 'copybooks',      label: 'Copybooks',  color: '#a78bfa' },
  { key: 'jcl',           label: 'JCL',        color: '#f59e0b' },
  { key: 'vsam',          label: 'VSAM',       color: '#06b6d4' },
  { key: 'db2_tables',    label: 'DB2',        color: '#9ece6a' },
]

function ApplicationComposition({ ingestSummary }) {
  const items = COMP_TYPES
    .map(t => ({ ...t, count: ingestSummary[t.key] || 0 }))
    .filter(t => t.count > 0)

  const total = items.reduce((s, t) => s + t.count, 0)

  if (!total) {
    return <div className="rod-empty-state">No asset data available</div>
  }

  return (
    <div className="rod-comp-list">
      {items.map(item => {
        const pct = Math.round((item.count / total) * 100)
        return (
          <div key={item.key} className="rod-comp-row">
            <span className="rod-comp-label">{item.label}</span>
            <div className="rod-comp-bar-wrap">
              <div
                className="rod-comp-bar-fill"
                style={{ width: `${pct}%`, background: item.color }}
              />
            </div>
            <span className="rod-comp-count">{item.count}</span>
            <span className="rod-comp-pct">{pct}%</span>
          </div>
        )
      })}
    </div>
  )
}

// ── Code Risk Ranking chart ───────────────────────────────────────────────────
// Horizontal bar: programs ranked by risk. Answers "where is the risk?"

function RiskRankingChart({ programs }) {
  const chartRef    = useRef(null)
  const instanceRef = useRef(null)

  // Top 10 by risk_score (or complexity as fallback), highest risk at top
  const ranked = useMemo(() => {
    const score = p =>
      p.risk_score != null ? p.risk_score * 100 :
      (p.cyclomatic_complexity || 0) + (p.lines_of_code || 0) / 100
    return [...programs]
      .sort((a, b) => score(b) - score(a))
      .slice(0, 12)
      .reverse() // ECharts y-axis renders bottom-up, so reverse for top-to-bottom display
  }, [programs])

  useEffect(() => {
    if (!chartRef.current || !ranked.length) return
    if (instanceRef.current) instanceRef.current.dispose()

    const chart = echarts.init(chartRef.current)
    instanceRef.current = chart

    const RISK_COLOR = { high: '#f87171', medium: '#f59e0b', low: '#9ece6a' }
    const riskScore = p =>
      p.risk_score != null ? Math.round(p.risk_score * 100) :
      Math.min(100, (p.cyclomatic_complexity || 0) * 4 + (p.fan_out || 0) * 2)

    const option = {
      backgroundColor: 'transparent',
      grid: { left: 120, right: 90, top: 8, bottom: 8, containLabel: false },
      tooltip: {
        trigger: 'item',
        backgroundColor: '#16161e',
        borderColor: '#3b4261',
        textStyle: { color: '#c0caf5', fontSize: 12 },
        formatter: (params) => {
          const p = ranked[params.dataIndex]
          return [
            `<strong>${p.program_name}</strong>`,
            `Risk: <span style="color:${RISK_COLOR[p.risk_tier] || '#565f89'}">${(p.risk_tier || '—').toUpperCase()}</span>`,
            `Lines of Code: ${(p.lines_of_code || 0).toLocaleString()}`,
            `Complexity: ${p.cyclomatic_complexity ?? '—'}`,
            `Outgoing Calls: ${p.fan_out ?? '—'}`,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        max: 100,
        show: false,
      },
      yAxis: {
        type: 'category',
        data: ranked.map(p => p.program_name),
        axisLabel: { color: '#a9b1d6', fontSize: 11, width: 110, overflow: 'truncate' },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      series: [{
        type: 'bar',
        barMaxWidth: 16,
        data: ranked.map(p => ({
          value: riskScore(p),
          itemStyle: {
            color: RISK_COLOR[p.risk_tier] || '#565f89',
            borderRadius: [0, 4, 4, 0],
          },
        })),
        label: {
          show: true,
          position: 'right',
          color: '#565f89',
          fontSize: 10,
          formatter: (params) => {
            const p = ranked[params.dataIndex]
            return `${(p.lines_of_code || 0).toLocaleString()} LOC`
          },
        },
      }],
    }

    chart.setOption(option)
    const onResize = () => chart.resize()
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('resize', onResize); chart.dispose() }
  }, [ranked])

  if (!ranked.length) {
    return <div className="rod-empty-state">No program data available</div>
  }

  const chartHeight = Math.max(200, ranked.length * 28)
  return <div ref={chartRef} style={{ width: '100%', height: `${chartHeight}px` }} />
}

// ── Risk distribution summary ─────────────────────────────────────────────────

function RiskSummary({ programs }) {
  const high   = programs.filter(p => p.risk_tier === 'high').length
  const medium = programs.filter(p => p.risk_tier === 'medium').length
  const low    = programs.filter(p => p.risk_tier === 'low').length
  const total  = programs.length

  if (!total) return null

  return (
    <div className="rod-risk-summary">
      {high   > 0 && <span className="rod-risk-chip high">{high} High</span>}
      {medium > 0 && <span className="rod-risk-chip medium">{medium} Medium</span>}
      {low    > 0 && <span className="rod-risk-chip low">{low} Low</span>}
      <span className="rod-risk-chip total">{total} programs</span>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function RunOverviewDashboard({
  accountId,
  appName,
  workflowRunId,
  onReportClick,
  reportGroups = [],
  getJobForType,
}) {
  const [loading,  setLoading]  = useState(true)
  const [runDoc,   setRunDoc]   = useState(null)
  const [programs, setPrograms] = useState([])

  useEffect(() => {
    if (!accountId || !appName) { setLoading(false); return }
    setLoading(true)

    const apiBase   = getAPIBaseUrl()
    const headers   = authHeaders()
    const fetchJson = (url) =>
      fetch(url, { headers }).then(r => r.ok ? r.json() : null).catch(() => null)

    const runsUrl = `${apiBase}/reporting/runs?account_id=${encodeURIComponent(accountId)}&app_name=${encodeURIComponent(appName)}&limit=20`

    fetchJson(runsUrl)
      .then(async data => {
        const runs = data?.runs || []
        if (!runs.length) { setLoading(false); return }

        const matched = workflowRunId
          ? (runs.find(r => r.workflow_run_id === workflowRunId) || runs[0])
          : runs[0]

        setRunDoc(matched)

        if (matched?.run_id) {
          const pd = await fetchJson(`${apiBase}/reporting/runs/${matched.run_id}/programs`)
          setPrograms(pd?.programs || [])
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [accountId, appName, workflowRunId])

  // ── Derived ───────────────────────────────────────────────────────────────
  const ingest   = runDoc?.ingest_summary     || {}
  const svcgen   = runDoc?.svcgen_summary     || {}
  const aiSum    = runDoc?.ai_naming_summary  || {}
  const preFlight = runDoc?.pre_flight_compile || {}
  const nodesCompleted = runDoc?.nodes_completed || []

  const totalAssets    = (ingest.cobol_programs || 0) + (ingest.copybooks || 0) + (ingest.jcl || 0) + (ingest.vsam || 0)
  const javaGenerated  = svcgen.services_generated ?? svcgen.java_files_generated ?? null
  const avgConfidence  = aiSum.avg_confidence != null ? `${Math.round(aiSum.avg_confidence * 100)}%` : null
  const transformedPct = programs.length > 0
    ? Math.round((programs.filter(p => p.cr_transform_status === 'completed').length / programs.length) * 100)
    : null

  const highRiskCount  = programs.filter(p => p.risk_tier === 'high').length
  const worstRisk      = highRiskCount > 0 ? 'HIGH' : programs.some(p => p.risk_tier === 'medium') ? 'MED' : programs.length > 0 ? 'LOW' : null
  const worstRiskColor = highRiskCount > 0 ? 'high' : programs.some(p => p.risk_tier === 'medium') ? 'medium' : 'low'

  const gateStatus = preFlight.passed === true ? 'PASS' : preFlight.passed === false ? 'FAIL' : null

  // ── Metric strip items ────────────────────────────────────────────────────
  const metrics = [
    { label: 'Programs',     value: ingest.cobol_programs ?? (programs.length || '—'),  color: null   },
    { label: 'Copybooks',    value: ingest.copybooks   ?? '—',                          color: null   },
    { label: 'JCL Jobs',     value: ingest.jcl         ?? '—',                          color: null   },
    { label: 'VSAM Files',   value: ingest.vsam        ?? '—',                          color: null   },
    { label: 'Java Files',   value: javaGenerated      ?? '—',                          color: null   },
    ...(avgConfidence ? [{ label: 'AI Naming',  value: avgConfidence, color: null }] : []),
    ...(worstRisk     ? [{ label: 'Risk',        value: worstRisk,     color: worstRiskColor }] : []),
    ...(gateStatus    ? [{ label: 'Quality Gate', value: gateStatus,   color: gateStatus === 'PASS' ? 'pass' : 'fail' }] : []),
  ]

  if (loading) {
    return (
      <div className="rod-root">
        <div className="rod-loading"><Loader2 size={22} className="spin" /><span>Loading…</span></div>
      </div>
    )
  }

  return (
    <div className="rod-root">

      {/* ── App header ── */}
      <div className="rod-header">
        <div className="rod-header-left">
          <h2 className="rod-app-name">{appName}</h2>
          {runDoc && (
            <span className={`rod-run-status rod-run-status--${runDoc.status || 'unknown'}`}>
              {runDoc.status || 'unknown'}
            </span>
          )}
          {runDoc?.started_at && (
            <span className="rod-run-date">{new Date(runDoc.started_at).toLocaleString()}</span>
          )}
          {!runDoc && (
            <span className="rod-no-mongo">No run data in MongoDB — workflow API only</span>
          )}
        </div>
      </div>

      {/* ── Metric strip — "What did we find?" ── */}
      {metrics.length > 0 && (
        <div className="rod-metric-strip">
          {metrics.map((m, i) => (
            <div key={i} className={`rod-metric-card ${m.color ? `rod-metric-card--${m.color}` : ''}`}>
              <div className="rod-metric-value">{m.value}</div>
              <div className="rod-metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pipeline — "Is it done?" ── */}
      <PipelineFlow nodesCompleted={nodesCompleted} />

      {/* ── Two-column: Composition + Run Summary ── */}
      <div className="rod-dash-row">

        {/* Application Composition — "What is this app made of?" */}
        <div className="rod-panel">
          <div className="rod-panel-header">
            <span className="rod-panel-title">Application Composition</span>
            {totalAssets > 0 && (
              <span className="rod-panel-badge">{totalAssets} total assets</span>
            )}
          </div>
          <ApplicationComposition ingestSummary={ingest} />
        </div>

        {/* Run Summary — "What was produced?" */}
        <div className="rod-panel">
          <div className="rod-panel-header">
            <span className="rod-panel-title">Transformation Summary</span>
          </div>
          <div className="rod-summary-rows">

            <div className="rod-summary-row">
              <span className="rod-summary-label">Quality Gate</span>
              {preFlight.passed === true && (
                <span className="rod-summary-value rod-gate-pass">
                  ✓ PASS
                  {preFlight.files_compiled != null ? ` · ${preFlight.files_compiled} files` : ''}
                </span>
              )}
              {preFlight.passed === false && (
                <span className="rod-summary-value rod-gate-fail">
                  ✗ FAIL · {preFlight.error_count ?? 0} error{preFlight.error_count !== 1 ? 's' : ''}
                </span>
              )}
              {preFlight.passed == null && (
                <span className="rod-summary-value rod-summary-muted">—</span>
              )}
            </div>

            <div className="rod-summary-row">
              <span className="rod-summary-label">Java Files Generated</span>
              <span className="rod-summary-value">
                {javaGenerated != null ? javaGenerated : <span className="rod-summary-muted">—</span>}
              </span>
            </div>

            {transformedPct != null && (
              <div className="rod-summary-row">
                <span className="rod-summary-label">Programs Transformed</span>
                <span className="rod-summary-value">{transformedPct}%</span>
              </div>
            )}

            {avgConfidence && (
              <div className="rod-summary-row">
                <span className="rod-summary-label">AI Naming Confidence</span>
                <span className="rod-summary-value" style={{ color: '#e0af68' }}>{avgConfidence}</span>
              </div>
            )}

            {aiSum.renamed_count != null && (
              <div className="rod-summary-row">
                <span className="rod-summary-label">Identifiers Renamed</span>
                <span className="rod-summary-value">{aiSum.renamed_count}</span>
              </div>
            )}

            {nodesCompleted.length > 0 && (
              <div className="rod-summary-row">
                <span className="rod-summary-label">Pipeline Stages</span>
                <span className="rod-summary-value">{nodesCompleted.length} / {PIPELINE_NODES.length} complete</span>
              </div>
            )}

          </div>
        </div>

      </div>

      {/* ── Code Risk Ranking — "Where is the risk?" ── */}
      {programs.length > 0 && (
        <div className="rod-panel">
          <div className="rod-panel-header">
            <span className="rod-panel-title">Code Risk Ranking</span>
            <span className="rod-panel-subtitle">Programs ranked by risk — highest at top · hover for details</span>
            <RiskSummary programs={programs} />
          </div>
          <RiskRankingChart programs={programs} />
        </div>
      )}

      {/* ── Open a Report — "What next?" ── */}
      <div className="rod-reports-section">
        <span className="rod-section-label">Open a Report</span>
        <div className="rod-report-grid">
          {reportGroups.map(group => (
            <React.Fragment key={group.id}>
              {group.label && (
                <div className="rod-report-group-label">{group.label}</div>
              )}
              {group.items.map(item => {
                const available = getJobForType ? !!getJobForType(item.requiredJob || item.id) : false
                return (
                  <button
                    key={item.id}
                    className={`rod-report-btn ${available ? 'available' : 'unavailable'}`}
                    onClick={() => available && onReportClick?.(item)}
                    disabled={!available}
                  >
                    <span className={`rod-dot ${available ? 'on' : ''}`} />
                    {item.label}
                    {available && <ChevronRight size={12} className="rod-btn-arrow" />}
                  </button>
                )
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

    </div>
  )
}
